import {
  Box,
  Button,
  ListItem,
  UnorderedList,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Alert, Modal } from '@gamma/ui/overlay';
import { i18n } from '@investigator/shared/localization';
import { useRemoveIdpDomain } from '@investigator/features/admin/queries/useRemoveIdpDomain';
import { GET_IDP_DOMAINS } from '@investigator/features/admin/queries/useGetIdpDomains';

const domainsText = i18n.pages.access.domainsList;

interface DeleteDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  domain: string;
}

export const DeleteDomainModal = ({
  isOpen,
  onClose,
  domain,
}: DeleteDomainModalProps) => {
  const toast = useToast();

  const [removeIdpDomain, { loading: isDeleting }] = useRemoveIdpDomain({
    onCompleted: () => {
      toast({
        status: 'success',
        title: domainsText.toasts.removeDomain.success.title,
        description: i18n.formatString(
          domainsText.toasts.removeDomain.success.description,
          domain,
        ),
        position: 'bottom-right',
        isClosable: true,
      });
      onClose();
    },
    onError: () => {
      toast({
        status: 'error',
        title: domainsText.toasts.removeDomain.error.title,
        description: i18n.formatString(
          domainsText.toasts.removeDomain.error.description,
          domain,
        ),
        position: 'bottom-right',
        isClosable: true,
      });
    },
    refetchQueries: [GET_IDP_DOMAINS],
  });

  const truncatedDomain =
    domain.length > 22 ? `${domain.slice(0, 6)}...${domain.slice(-6)}` : domain;

  const handleClose = () => {
    if (isDeleting) return;
    onClose();
  };

  const handleDelete = () => {
    removeIdpDomain({ variables: { idp_domain: domain } });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!isDeleting}
      closeOnEsc={!isDeleting}
      title={i18n.formatString(domainsText.removeDomainHeader, truncatedDomain)}
      data-testid="delete-domain-modal"
      body={
        <VStack spacing={4} align="stretch">
          <Alert
            status="warning"
            variant="subtle"
            title={
              <UnorderedList>
                <ListItem fontWeight="normal">
                  {domainsText.removeWarning1}
                </ListItem>
                <ListItem fontWeight="normal">
                  {domainsText.removeWarning2}
                </ListItem>
              </UnorderedList>
            }
          ></Alert>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
            <Button
              variant="solid"
              colorScheme="gray"
              onClick={handleClose}
              isDisabled={isDeleting}
              data-testid="cancel-delete-button"
            >
              {domainsText.cancel}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={handleDelete}
              isLoading={isDeleting}
              loadingText={domainsText.removing}
              isDisabled={isDeleting}
              data-testid="confirm-delete-button"
            >
              {domainsText.removeDomain}
            </Button>
          </Box>
        </VStack>
      }
    ></Modal>
  );
};
